import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Hourglass, PlayCircle } from 'lucide-react';

interface LiveClockProps {
  customColor?: string;
  event?: any;
}

const LiveClock: React.FC<LiveClockProps> = (props) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getTimeParts = (date: Date) => {
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    const parts = timeStr.split(' ');
    return {
      time: parts[0] || timeStr,
      period: parts[1] || ''
    };
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const { customColor, event } = props;
  const primaryColor = customColor || '#0d9488'; // Default to teal-600

  // Calculate days left & start time for event
  const getEventCountdownInfo = () => {
    if (!event?.start_date) return null;

    const now = new Date();
    const startDate = new Date(event.start_date);

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const eventDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());

    const diffTime = eventDay.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Formatted start time
    const startTimeStr = startDate.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    if (diffDays === 0) {
      return { label: 'Today!', subtext: `Starts at ${startTimeStr}`, isToday: true, days: 0 };
    } else if (diffDays > 0) {
      return {
        label: `${diffDays} ${diffDays === 1 ? 'Day Left' : 'Days Left'}`,
        subtext: `Starts at ${startTimeStr}`,
        isToday: false,
        days: diffDays
      };
    } else {
      return { label: 'In Progress / Completed', subtext: `Started at ${startTimeStr}`, isToday: false, days: diffDays };
    }
  };

  const countdown = getEventCountdownInfo();
  const timeParts = getTimeParts(currentTime);

  return (
    <div
      className="rounded-xl shadow-lg p-5 sm:p-6 text-white border transition-colors duration-300 w-full overflow-hidden"
      style={{
        backgroundColor: primaryColor,
        borderColor: primaryColor
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="text-xs sm:text-sm font-medium">Live</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
        </div>
      </div>

      {/* Greeting */}
      <div className="mb-2 sm:mb-3">
        <h3 className="text-base sm:text-lg font-semibold">{getGreeting()}</h3>
      </div>

      {/* Responsive Time Display */}
      <div className="mb-3 sm:mb-4 overflow-hidden">
        <div className="flex items-baseline flex-wrap gap-1.5 sm:gap-2">
          <span className="text-2xl min-[360px]:text-3xl sm:text-4xl lg:text-4xl font-bold tracking-tight font-mono break-all sm:break-normal">
            {timeParts.time}
          </span>
          {timeParts.period && (
            <span className="text-xs sm:text-sm font-bold font-mono px-2 py-0.5 bg-white/20 rounded-md self-center">
              {timeParts.period}
            </span>
          )}
        </div>
      </div>

      {/* Date Display */}
      <div
        className="pt-3 border-t text-xs sm:text-sm"
        style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}
      >
        <p className="opacity-95">{formatDate(currentTime)}</p>
      </div>

      {/* Event Days Left & Start Time Banner */}
      {countdown && (
        <div className="mt-3 pt-3 border-t border-white/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/25 backdrop-blur-sm text-xs font-bold tracking-wide">
            {countdown.isToday ? (
              <PlayCircle className="h-3.5 w-3.5 text-amber-300 animate-pulse" />
            ) : (
              <Hourglass className="h-3.5 w-3.5 text-white/90" />
            )}
            <span>{countdown.label}</span>
          </div>
          {countdown.subtext && (
            <span className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-white/90 font-medium">
              <Clock className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{countdown.subtext}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveClock;
